import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Box, Row } from '../../legacy-design-system/form'
import { css } from '@emotion/css'
import { Dropdown } from '@contrast-security-inc/design-system-inputs'
import { Text } from '@contrast-security-inc/design-system-typography'
import { Button } from '@contrast-security-inc/design-system-buttons'
import {
  TTRTimeFilterType,
  TTRTimeFilterValue,
  TTRTimeFilterValues
} from './index'
import { useUpdateVulnerabilityTtrTimeFilterType } from './use-update-vulnerability-ttr-time-filter-type'
import { toast } from '@contrast-security-inc/design-system-alerts'
import { useGetVulnerabilityTTRProperties } from './api/get-vulnerability-ttr-properties'
import { useQueryClient } from 'react-query'
import { Query } from '../../hooks/queries'

type TTRTimeFilterTypeFormProps = {
  readonly organizationUuid: string
}

export function TtrTimeFilterTypeForm({
  organizationUuid
}: TTRTimeFilterTypeFormProps) {
  const queryClient = useQueryClient()

  const [vulnerabilityTTRTimeFilterType, setVulnerabilityTTRTimeFilterType] =
    React.useState<TTRTimeFilterType | undefined>(undefined)

  const { formatMessage } = useIntl()
  const ttrTimeFilterValues = TTRTimeFilterValues

  const { isLoading, data } = useGetVulnerabilityTTRProperties({
    organizationUuid
  })

  const { mutate: updateTtrTimeFilter, isLoading: isUpdating } =
    useUpdateVulnerabilityTtrTimeFilterType({
      onSuccess: async () => {
        await queryClient.invalidateQueries([
          Query.VULNERABILITY_TTR_PROPERTIES,
          organizationUuid
        ])

        toast(
          formatMessage({
            id: 'detail_vulnerabilityTtrTimeFilter_updateSuccessMsg'
          })
        )
      },
      onError: () => {
        toast(
          formatMessage({
            id: 'detail_vulnerabilityTtrTimeFilter_updateErrorMsg'
          }),
          {
            variant: 'error'
          }
        )
      }
    })

  React.useEffect(() => {
    if (data) {
      setVulnerabilityTTRTimeFilterType(data.vulnerabilityTTRTimeFilterType)
    }
  }, [data])

  return vulnerabilityTTRTimeFilterType ? (
    <>
      <Row>
        <Box basis={4}>
          <div
            className={css`
              margin-top: 15px;
              margin-left: 12px;
            `}
          >
            <Dropdown<TTRTimeFilterValue>
              maxHeight="300px"
              items={ttrTimeFilterValues}
              itemToID={(item) => item.id}
              label={formatMessage({
                id: 'title_ttrTimeFilter_selectOption'
              })}
              value={ttrTimeFilterValues.find(
                (item) => item.id === vulnerabilityTTRTimeFilterType
              )}
              itemToString={({ title }) => formatMessage({ id: title })}
              renderInputValue={({ title }) => (
                <Text>{formatMessage({ id: title })}</Text>
              )}
              onChange={(_evt, selectedItem) => {
                if (selectedItem) {
                  setVulnerabilityTTRTimeFilterType(selectedItem.id)
                }
              }}
              testId="ttr-time-filter-value-select"
            />
          </div>
        </Box>
      </Row>
      <div
        className={css`
          display: flex;
          flex-direction: row-reverse;
        `}
      >
        <FormattedMessage id={'action_save'}>
          {(saveMessage) => (
            <Button
              testId="save-ttr-properties"
              disabled={isLoading || isUpdating}
              onClick={() => {
                updateTtrTimeFilter({
                  organizationUuid,
                  vulnerabilityTTRTimeFilterType
                })
              }}
            >
              {saveMessage}
            </Button>
          )}
        </FormattedMessage>
      </div>
    </>
  ) : (
    <></>
  )
}
